/**
 * System Config Cache Service
 *
 * Isolated, stateless module for all localStorage branding persistence.
 * Applies three-layer tamper defence on every read.
 * No React, no Zustand — pure utility so it is testable in isolation.
 */

import logger from '../services/loggerService';
import {
  GENERAL_CONFIG_CACHE_VERSION,
  GENERAL_CONFIG_STORAGE_KEY,
  IDENTITY_DEFAULTS,
  SYSTEM_CONFIG_CTX,
} from '../constants/systemConfigConstants';
import { CachedGeneralConfig } from '../types/systemConfig.types';
import { EmailConfiguration, SmsConfiguration } from '../types';
import { BackendSystemConfig } from '../types/systemConfig.types';

// ── Type guard ────────────────────────────────────────────────────────────────

/**
 * Validates that an unknown parsed value is a structurally correct
 * CachedGeneralConfig. Protects against tampered or malformed cache entries.
 */
export function isValidCachedGeneralConfig(v: unknown): v is CachedGeneralConfig {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.universityName === 'string' &&
    typeof r.systemName     === 'string' &&
    typeof r.logoUrl        === 'string' &&
    typeof r._cacheVersion  === 'number'
  );
}

// ── Read ──────────────────────────────────────────────────────────────────────

/**
 * Three-layer defensive localStorage read:
 *  1. JSON parse failure  → evict entry, return env defaults
 *  2. Shape guard failure → evict entry, return env defaults (tamper protection)
 *  3. Version mismatch    → evict entry, return env defaults (schema migration)
 *
 * No TTL — branding persists until the admin explicitly updates it.
 */
export function getStoredGeneralConfig(): CachedGeneralConfig {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return IDENTITY_DEFAULTS;
    }

    const raw = localStorage.getItem(GENERAL_CONFIG_STORAGE_KEY);
    if (!raw) return IDENTITY_DEFAULTS;

    // Layer 1: JSON parse
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (parseError) {
      logger.warn('General config cache is not valid JSON — evicting', {
        error: parseError,
        context: SYSTEM_CONFIG_CTX.GET,
      });
      localStorage.removeItem(GENERAL_CONFIG_STORAGE_KEY);
      return IDENTITY_DEFAULTS;
    }

    // Layer 2: Shape validation
    if (!isValidCachedGeneralConfig(parsed)) {
      logger.warn('General config cache failed shape validation — evicting', {
        parsedKeys: parsed !== null && typeof parsed === 'object'
          ? Object.keys(parsed as object)
          : 'non-object',
        context: SYSTEM_CONFIG_CTX.GET,
      });
      localStorage.removeItem(GENERAL_CONFIG_STORAGE_KEY);
      return IDENTITY_DEFAULTS;
    }

    // Layer 3: Cache version guard (schema migration)
    if (parsed._cacheVersion !== GENERAL_CONFIG_CACHE_VERSION) {
      logger.info('General config cache version mismatch — evicting stale entry', {
        stored:  parsed._cacheVersion,
        current: GENERAL_CONFIG_CACHE_VERSION,
        context: SYSTEM_CONFIG_CTX.GET,
      });
      localStorage.removeItem(GENERAL_CONFIG_STORAGE_KEY);
      return IDENTITY_DEFAULTS;
    }

    return parsed;
  } catch (e) {
    logger.warn('Unexpected error reading general config from localStorage', {
      error: e,
      context: SYSTEM_CONFIG_CTX.GET,
    });
    return IDENTITY_DEFAULTS;
  }
}

// ── Write ─────────────────────────────────────────────────────────────────────

/**
 * Writes a validated, version-stamped branding entry to localStorage.
 * Coerces individual fields rather than writing raw API payloads — prevents
 * unexpected types or extra keys from entering the cache.
 *
 * Fire-and-forget: localStorage is a resilience layer, never a hard dependency.
 */
export function persistGeneralConfig(general: Record<string, unknown>): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;

    const entry: CachedGeneralConfig = {
      universityName: typeof general.universityName === 'string'
        ? general.universityName
        : IDENTITY_DEFAULTS.universityName,
      systemName: typeof general.systemName === 'string'
        ? general.systemName
        : IDENTITY_DEFAULTS.systemName,
      logoUrl: typeof general.logoUrl === 'string'
        ? general.logoUrl
        : IDENTITY_DEFAULTS.logoUrl,
      _cacheVersion: GENERAL_CONFIG_CACHE_VERSION,
    };

    localStorage.setItem(GENERAL_CONFIG_STORAGE_KEY, JSON.stringify(entry));
  } catch (e) {
    logger.warn('Failed to persist general config to localStorage', {
      error: e,
      context: SYSTEM_CONFIG_CTX.PERSIST,
    });
  }
}

// ── Notification resolver ─────────────────────────────────────────────────────

/**
 * Resolves the canonical email/sms config from a raw backend notification object.
 * Resolution chain: raw.email → raw.emailConfig → callerFallback → hard defaults.
 * All four levels guarantee a non-undefined result — no cast needed at call sites.
 */
export function resolveNotifications(
  raw: NonNullable<BackendSystemConfig['notifications']>,
  fallbackEmail: EmailConfiguration | undefined,
  fallbackSms:   SmsConfiguration   | undefined,
  hardDefaultEmail: EmailConfiguration,
  hardDefaultSms:   SmsConfiguration,
): { emailConfig: EmailConfiguration; smsConfig: SmsConfiguration } {
  return {
    emailConfig: (raw.email ?? raw.emailConfig ?? fallbackEmail ?? hardDefaultEmail) as EmailConfiguration,
    smsConfig:   (raw.sms   ?? raw.smsConfig   ?? fallbackSms   ?? hardDefaultSms)   as SmsConfiguration,
  };
}
